import {WriteButton} from '../common/buttons/WriteActions';
import {Alert, Box, MenuItem, Stack, TextField, Typography} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {FormEvent, useMemo, useState} from 'react';
import {feedbackAPI} from '../../services/FeedbackServices';
import {FeedbackContext, FeedbackType} from '../../models/Feedback';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorMessage} from '../../common/apiErrors';
import {APP_VERSION} from '../../common/appVersion';
import ScreenshotPicker from './ScreenshotPicker';
import {buildFeedbackPayload} from './feedbackPayload';

const types: FeedbackType[] = ['Bug', 'Suggestion', 'Question', 'Other'];

export default function FeedbackForm({context}: {context?: FeedbackContext}) {
    const {t} = useAppTranslation();
    const [sendFeedback, request] = feedbackAPI.useSendFeedbackMutation();
    const [uploadScreenshot, screenshotRequest] = feedbackAPI.useUploadFeedbackScreenshotMutation();
    const [createdId, setCreatedId] = useState('');
    const busy = request.isLoading || screenshotRequest.isLoading;
    const [type, setType] = useState<FeedbackType>(context?.type ?? 'Suggestion');
    const [message, setMessage] = useState('');
    const [screenshot, setScreenshot] = useState<File | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [sent, setSent] = useState(false);
    const errors = useMemo(() => ({
        message: !message.trim() ? String(t('feedback:validation.message_required' as never)) : message.length > 8000 ? String(t('feedback:validation.message_length' as never)) : '',
    }), [message, t]);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        setSubmitted(true);
        setSent(false);
        if (errors.message) return;
        const currentPage = (context?.currentPage || window.location.pathname).slice(0, 500);
        const body = buildFeedbackPayload({
            type,
            message,
            context: {...context, currentPage, appVersion: context?.appVersion || APP_VERSION},
        });
        try {
            const id = createdId || (await sendFeedback(body).unwrap()).id;
            setCreatedId(id);
            if (screenshot) await uploadScreenshot({id, file: screenshot}).unwrap();
            setCreatedId('');
            setMessage('');
            setScreenshot(null);
            setSubmitted(false);
            setSent(true);
        } catch { /* request state is rendered below */ }
    };

    return <Box component="form" onSubmit={submit} noValidate>
        <Stack spacing={2.5}>
            {context?.relatedTraceId && <Alert severity="info">{t('feedback:error_context_attached' as never)}</Alert>}
            {sent && <Alert severity="success">{t('feedback:success' as never)}</Alert>}
            {(request.error || screenshotRequest.error) && <Alert severity="error">{getApiErrorMessage(request.error || screenshotRequest.error, t)}</Alert>}
            <TextField select required fullWidth label={t('feedback:type' as never)} value={type} disabled={busy || Boolean(createdId)} onChange={event => setType(event.target.value as FeedbackType)}>
                {types.map(value => <MenuItem key={value} value={value}>{t(`feedback:types.${value}` as never)}</MenuItem>)}
            </TextField>
            <TextField required fullWidth multiline minRows={5} maxRows={14} label={t('feedback:message' as never)} placeholder={t('feedback:message_placeholder' as never)} value={message} disabled={busy || Boolean(createdId)} onChange={event => {setMessage(event.target.value); request.reset(); setSent(false);}} error={submitted && Boolean(errors.message)} helperText={(submitted && errors.message) || `${message.length}/8000`} inputProps={{maxLength: 8001}}/>
            <ScreenshotPicker file={screenshot} onChange={setScreenshot} disabled={busy}/>
            <Typography variant="caption" color="text.secondary">{t('feedback:privacy_hint' as never)}</Typography>
            <Box><WriteButton type="submit" variant="contained" disabled={busy} endIcon={<SendIcon/>}>{busy ? t('feedback:sending' as never) : t('feedback:send' as never)}</WriteButton></Box>
        </Stack>
    </Box>;
}
